import React from 'react';
import styled from 'styled-components';

// Generate a CSS keyframes string for the ribbon animation
const ribbonKeyframesString = (startPct, endPct) => `
  0% { transform: translateX(-120px) skewX(-12deg); }
  ${startPct}% { transform: translateX(-120px) skewX(-12deg); }
  ${endPct}% { transform: translateX(150px) skewX(-12deg); }
  100% { transform: translateX(150px) skewX(-12deg); }
`;

const Wrapper = styled.div`
  width: 82px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { overflow: visible; }
  .desktop-only { display: block; }
  .mobile-only { display: none; }

  @media (max-width:900px) {
    .desktop-only { display: none; }
    .mobile-only { display: block; }
  }
`;

const Connector = ({ index = 0, count = 1 }) => {
  const safeCount = Math.max(1, Number(count));
  const startPct = (index / safeCount) * 100;
  const slotPct = 100 / safeCount; // percentage width of this connector's slot
  const moveFraction = 0.08; // base fraction used previously (fast sweep)
  const slowFactor = 7; // slow down by 7x as requested
  const scaledMoveFraction = Math.min(1, moveFraction * slowFactor);
  const endMovePct = startPct + slotPct * scaledMoveFraction;
  const totalCycle = safeCount * 5; // full cycle length in seconds (5s per slot)

  // Create a unique classname and inline style tag that defines the animation using generated keyframes
  const className = `connector-dynamic-${index}`;
  const animName = `connectorAnim${index}`;
  const keyframeCSS = `@keyframes ${animName} { ${ribbonKeyframesString(startPct, endMovePct).replace(/\n/g,' ')} }`;
  // scope desktop animation to desktop-only SVG so mobile rules don't override it
  const cssRule = `.${className} .desktop-only .ribbon { animation: ${animName} ${totalCycle}s linear infinite; will-change: transform; }`;

  // vertical keyframes for mobile (translateY)
  const ribbonKeyframesStringV = (startPctV, endPctV) => `
    0% { transform: translateY(-120px) skewY(-6deg); }
    ${startPctV}% { transform: translateY(-120px) skewY(-6deg); }
    ${endPctV}% { transform: translateY(150px) skewY(-6deg); }
    100% { transform: translateY(150px) skewY(-6deg); }
  `;
  const animNameV = `connectorAnimV${index}`;
  const keyframeCSSV = `@keyframes ${animNameV} { ${ribbonKeyframesStringV(startPct, endMovePct).replace(/\n/g,' ')} }`;
  // scope mobile animation to mobile-only SVG
  const cssRuleV = `.${className} .mobile-only .ribbon { animation: ${animNameV} ${totalCycle}s linear infinite; will-change: transform; }`;

  return (
    <Wrapper className={className} aria-hidden>
      <style>{keyframeCSS + '\n' + cssRule + '\n' + keyframeCSSV + '\n' + cssRuleV}</style>
      {/* Desktop horizontal arrow */}
      <svg className="desktop-only" width="90" height="64" viewBox="0 0 90 64" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
          <linearGradient id={`gradConnectorBG-${index}`} x1="0" x2="1">
            <stop offset="0%" stopColor="#000000" />
            <stop offset="100%" stopColor="#3E8F93" />
          </linearGradient>
          <linearGradient id={`gradOrangeRibbon-${index}`} x1="0" x2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <filter id={`shadow-connector-${index}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.12" />
          </filter>
          <clipPath id={`arrowClip-${index}`}>
            <rect x="6" y="28" width="48" height="8" rx="4" />
            <polygon points="57,24 75,32 57,40" />
          </clipPath>
        </defs>

        {/* base arrow (black -> green) */}
        <g filter={`url(#shadow-connector-${index})`}>
          <rect x="6" y="28" width="48" height="8" rx="4" fill={`url(#gradConnectorBG-${index})`} />
          <polygon points="57,24 75,32 57,40" fill={`url(#gradConnectorBG-${index})`} />
        </g>

        {/* animated green ribbon clipped to arrow shape (desktop) */}
        <g clipPath={`url(#arrowClip-${index})`}>
          <rect className="ribbon" x="-120" y="26" width="60" height="9" fill="#3E8F93" opacity="0.7" transform="skewX(-12)" />
          <rect className="ribbon" x="-30" y="26" width="60" height="9" fill="#3E8F93" opacity="0.7" transform="skewX(-12)" />
        </g>
      </svg>

      {/* Mobile vertical arrow (shown under 900px) */}
      <svg className="mobile-only" width="40" height="80" viewBox="0 0 40 80" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
          <clipPath id={`arrowClipV-${index}`}>
            <rect x="16" y="6" width="8" height="40" rx="4" />
            <polygon points="12,48 28,48 20,64" />
          </clipPath>
          <linearGradient id={`gradConnectorBGV-${index}`} x1="0" x2="1">
            <stop offset="0%" stopColor="#000000" />
            <stop offset="100%" stopColor="#3E8F93" />
          </linearGradient>
          <filter id={`shadow-connector-v-${index}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.12" />
          </filter>
        </defs>

        <g filter={`url(#shadow-connector-v-${index})`}>
          <rect x="16" y="6" width="8" height="40" rx="4" fill={`url(#gradConnectorBGV-${index})`} />
          <polygon points="12,48 28,48 20,64" fill={`url(#gradConnectorBGV-${index})`} />
        </g>

        <g clipPath={`url(#arrowClipV-${index})`}>
          <rect className="ribbon" x="14" y="-120" width="12" height="80" fill="#3E8F93" opacity="0.7" transform="skewY(-6)" />
          <rect className="ribbon" x="14" y="-40" width="12" height="80" fill="#3E8F93" opacity="0.7" transform="skewY(-6)" />
        </g>
      </svg>
    </Wrapper>
  );
};

export default Connector;
